/**
 * Hộp thoại *Thêm tên miền cho người dùng* — FR-073, TC-25.
 * Bám `permissions.html` → `#modal-add-domain`:
 *   .field (select tên miền chưa gán) · .field (mức quyền) · .setting-row (thời hạn)
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { Modal } from '../../components/Modal';
import { SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';
import { permApi, permKeys, type PermissionUserView } from './api';

export function AddDomainToUserDialog({
  open,
  user,
  onClose,
}: {
  open: boolean;
  user: PermissionUserView;
  onClose: () => void;
}) {
  const t = useT();
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();

  const [domainId, setDomainId] = useState('');
  const [roleId, setRoleId] = useState('');

  const freeQuery = useQuery({
    queryKey: permKeys.grantableDomains(user.id),
    queryFn: () => permApi.grantableDomains(user.id),
    enabled: open,
  });
  const rolesQuery = useQuery({
    queryKey: permKeys.domainRoles,
    queryFn: permApi.domainRoles,
    staleTime: 10 * 60 * 1000,
    enabled: open,
  });

  const free = freeQuery.data ?? [];
  const assignable = (rolesQuery.data ?? []).filter((role) => role.assignable);

  useEffect(() => {
    if (!domainId && free[0]) setDomainId(free[0].id);
  }, [free, domainId]);
  useEffect(() => {
    if (!roleId && assignable.length > 0) {
      setRoleId(assignable.find((role) => role.isDefault)?.id ?? assignable[0]!.id);
    }
  }, [assignable, roleId]);

  const grant = useMutation({
    mutationFn: () => permApi.createGrant(domainId, { userId: user.id, domainRoleId: roleId }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: permKeys.all });
      const domain = free.find((row) => row.id === domainId);
      toast(
        t('perm.granted', {
          domain: domain?.name ?? '',
          name: created.user.fullName || created.user.email,
        }),
      );
      setDomainId('');
      onClose();
    },
    onError: (err) => toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  return (
    <>
      {/* Giữ đúng `data-od-id` của prototype cho selector E2E (docs/08 §8.9). */}
      <div data-od-id="modal-add-domain" hidden />
      <Modal
        open={open}
        title={t('perm.addDomainTitle')}
        subtitle={t('perm.addDomainSub', { name: user.fullName || user.email })}
        onClose={onClose}
        onSubmit={() => {
          if (domainId) grant.mutate();
        }}
        footer={
          <>
            <button className="btn" type="button" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={!domainId || grant.isPending}
              data-od-id="add-domain-submit"
            >
              {grant.isPending ? t('common.processing') : t('perm.grant')}
            </button>
          </>
        }
      >
        {freeQuery.isLoading || rolesQuery.isLoading ? (
          <SkeletonCard rows={3} />
        ) : (
          <>
            <div className="field">
              <label htmlFor="ad-domain">{t('perm.addDomainField')}</label>
              <select
                id="ad-domain"
                value={domainId}
                disabled={free.length === 0}
                onChange={(event) => setDomainId(event.target.value)}
              >
                {free.length === 0 ? (
                  <option value="">{t('perm.addDomainNoneOption')}</option>
                ) : (
                  free.map((domain) => (
                    <option key={domain.id} value={domain.id}>
                      {domain.name}
                      {domain.owner
                        ? ` — ${t('perm.ownedBy', { name: domain.owner.fullName })}`
                        : ''}
                    </option>
                  ))
                )}
              </select>
              <span className="hint">
                {free.length > 0
                  ? t('perm.addDomainHint', { count: free.length })
                  : t('perm.addDomainNone')}
              </span>
            </div>

            <div className="field">
              <label htmlFor="ad-level">{t('perm.col.level')}</label>
              <select
                id="ad-level"
                value={roleId}
                onChange={(event) => setRoleId(event.target.value)}
              >
                {assignable.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            {/*
              Prototype còn một `.setting-row` "Đặt thời hạn". Không FR nào
              (FR-070…085) yêu cầu, `domain_grants` không có cột hạn, và job tự
              thu hồi thuộc worker M3+. Theo docs/11 §11.8.1 — "prototype có mà
              FR không có thì ghi OPEN-QUESTIONS và KHÔNG dựng" — ô này được bỏ
              hẳn thay vì dựng rồi khoá. Xem OQ-053.
            */}
          </>
        )}
      </Modal>
    </>
  );
}
